import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';

import type { AnalyticsKeyValue, AnalyticsService } from '$src/utils/analytics/types';

const isDatadogEnabled = () =>
  Boolean(process?.env?.NEXT_PUBLIC_DATADOG_APPLICATION_ID && process?.env?.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN);

const initDatadog = () => {
  if (!isDatadogEnabled()) {
    return;
  }

  // Ensure it runs only on the client side
  if (typeof window === 'undefined') return;

  datadogRum.init({
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID as string,
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN as string,
    site: 'datadoghq.eu',
    service: 'checkout-form-frontend',
    env: process?.env?.NEXT_PUBLIC_ENV || 'development',
    sessionSampleRate: 5, // Collects all sessions (adjust as needed)
    sessionReplaySampleRate: 0, // Enables session replay for 0% of users
    defaultPrivacyLevel: 'mask-user-input',
    plugins: [reactPlugin({ router: false })],
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    allowedTracingUrls: [
      { match: `${window.location.origin}/api/`, propagatorTypes: ['datadog', 'tracecontext'] },
      { match: `${process.env.NEXT_PUBLIC_JUST_API_URL}`, propagatorTypes: ['datadog', 'tracecontext'] },
    ],
  });

  datadogLogs.init({
    clientToken: process?.env?.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN as string,
    site: 'datadoghq.eu',
    service: 'checkout-form-frontend',
    env: process?.env?.NEXT_PUBLIC_ENV || 'development',
    forwardErrorsToLogs: true,
    sessionSampleRate: 5,
  });

  datadogRum.startSessionReplayRecording();
};

const trackDatadogEvent = (name: string, props?: AnalyticsKeyValue) => {
  if (!isDatadogEnabled()) {
    return;
  }

  datadogRum.addAction(name, props);
};

const identifyDatadogUser = (userId: string, props?: AnalyticsKeyValue) => {
  if (!isDatadogEnabled()) {
    return;
  }

  datadogRum.setUser({
    id: userId,
    ...props,
  });
};

const setDatadogUserProperties = (props: AnalyticsKeyValue) => {
  if (!isDatadogEnabled()) {
    return;
  }

  Object.entries(props).forEach(([key, value]) => {
    datadogRum.setUserProperty(key, value);
  });
};

export const datadog: AnalyticsService = {
  init: initDatadog,
  isEnabled: isDatadogEnabled,
  trackEvent: trackDatadogEvent,
  identifyUser: identifyDatadogUser,
  setUserProperties: setDatadogUserProperties,
};
